<script setup>
import { ldJson } from '@/lib/jsonLd';
import { Head, Link } from '@inertiajs/vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import { useCopy } from '@/composables/useCopy';
const props = defineProps({
    domain: Object,
    service: String,
    branding: Object,
    designVariant: Object,
    jobs: { type: Array, default: () => [] },
    cities: { type: Array, default: () => [] },
    seoTitle: String,
    metaDescription: String,
    jsonLd: Object,
    copyPack: { type: Object, default: () => ({}) },
    spotImages: { type: Object, default: () => ({}) },
});

const { t, ts, spot } = useCopy(props);
</script>

<template>
    <Head :title="seoTitle">
        <meta name="description" :content="metaDescription" />
        <meta property="og:title" :content="seoTitle" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json" v-if="jsonLd" v-html="ldJson(jsonLd)" />
    </Head>
    <AppLayout :domain="domain || {}" activePage="karriere" heroMode>
        <!-- Dark hero -->
        <section class="sv-kj-hero">
            <div class="sv-kj-hero__inner">
                <p class="sv-kj-hero__tag">{{ t('karriere_index_hero_tag', 'Karriere') }}</p>
                <h1 class="sv-kj-hero__title">{{ t('karriere_index_hero_title_pre', 'Werde Teil unseres') }} <span class="sv-kj-hero__accent">{{ t('karriere_index_hero_title_accent', 'wachsenden Teams') }}</span></h1>
                <p class="sv-kj-hero__sub">
                    {{ t('karriere_index_hero_sub_pre', 'Wir suchen aktuell motivierte Minijobber im Photovoltaik-Vertrieb in') }}
                    <strong>{{ cities.join(', ') }}</strong>.
                    {{ t('karriere_index_hero_sub_post', 'Flexible Arbeitszeiten, faire Bezahlung und sinnstiftende Arbeit.') }}
                </p>
            </div>
        </section>

        <!-- Jobs listing -->
        <section class="sv-kj-body">
            <div class="sv-kj-container">
                <div v-if="jobs.length === 0" class="sv-kj-empty">
                    <p>{{ t('karriere_index_empty', 'Aktuell keine offenen Stellen. Schau bald wieder vorbei!') }}</p>
                </div>
                <div v-else class="sv-kj-grid">
                    <Link
                        v-for="job in jobs"
                        :key="job.id"
                        :href="`/karriere/${job.slug}`"
                        class="sv-kj-card"
                    >
                        <div v-if="job.is_featured" class="sv-kj-card__badge">{{ t('karriere_index_card_featured', 'Empfohlen') }}</div>
                        <h2 class="sv-kj-card__title">{{ job.title }}</h2>
                        <p class="sv-kj-card__meta">{{ job.city }} | {{ job.employment_type }} | {{ job.hours_per_week }}{{ t('karriere_index_card_hours', 'h/Woche') }}</p>
                        <p v-if="job.salary_range" class="sv-kj-card__salary">{{ job.salary_range }}</p>
                        <p class="sv-kj-card__summary">{{ job.summary }}</p>
                        <span class="sv-kj-card__link">{{ t('karriere_index_card_link', 'Details ansehen -->') }}</span>
                    </Link>
                </div>
            </div>
        </section>
</AppLayout>
</template>

<style scoped>
.sv-kj-hero { background: #0a1628; padding: 120px 24px 80px; text-align: left; }
.sv-kj-hero__inner { max-width: 780px; margin: 0 auto; }
.sv-kj-hero__tag { font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 700; color: var(--brand-accent, #00d782); text-transform: uppercase; letter-spacing: 2px; margin-bottom: 16px; }
.sv-kj-hero__title { font-family: 'Inter', sans-serif; font-size: clamp(28px, 4vw, 44px); font-weight: 800; color: #ffffff; line-height: 1.15; margin-bottom: 16px; }
.sv-kj-hero__accent { color: var(--brand-accent, #00d782); }
.sv-kj-hero__sub { font-family: 'Inter', sans-serif; font-size: 17px; color: #94a3b8; line-height: 1.7; max-width: 560px; }
.sv-kj-hero__sub strong { color: #ffffff; }

.sv-kj-body { background: #f0f4f8; padding: 60px 24px 100px; }
.sv-kj-container { max-width: 800px; margin: 0 auto; }
.sv-kj-empty { text-align: center; padding: 60px 0; font-family: 'Inter', sans-serif; font-size: 16px; color: #64748b; }

.sv-kj-grid { display: flex; flex-direction: column; gap: 16px; }
.sv-kj-card { display: block; background: #ffffff; padding: 28px; border-radius: 16px; text-decoration: none; box-shadow: 0 2px 12px rgba(0,0,0,0.04); transition: transform 200ms, box-shadow 200ms; position: relative; }
.sv-kj-card:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(0,0,0,0.08); }
.sv-kj-card__badge { position: absolute; top: 16px; right: 16px; padding: 4px 12px; background: var(--brand-accent, #00d782); color: #1a1a1a; font-family: 'Inter', sans-serif; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; border-radius: 8px; }
.sv-kj-card__title { font-family: 'Inter', sans-serif; font-size: 18px; font-weight: 700; color: #1a1a1a; margin-bottom: 8px; padding-right: 100px; }
.sv-kj-card__meta { font-family: 'Inter', sans-serif; font-size: 13px; color: #64748b; margin-bottom: 4px; }
.sv-kj-card__salary { font-family: 'Inter', sans-serif; font-size: 14px; font-weight: 600; color: var(--brand-accent, #00d782); margin-bottom: 8px; }
.sv-kj-card__summary { font-family: 'Inter', sans-serif; font-size: 14px; color: #475569; line-height: 1.6; margin-bottom: 12px; }
.sv-kj-card__link { font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 700; color: var(--brand-accent, #00d782); }
</style>
